import React, { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { TitledBody } from '../../common/components/TitledBody'
import { VpUser } from '../../app/types'
import { useParams } from 'react-router'
import { api } from '../../common/api'
import { useSelector } from 'react-redux'
import { customerOriginIdSelector } from '../../authentication/redux'
import { Page } from '../../app/navigation/redux'
import { SectionedForm } from '../../common/components/SectionedForm'
import { Grid } from '@mui/material'
import { usePrivilege } from '../../common/util/hooks'
import { FeaturePrivileges } from '../../common/util/featurePrivileges'
import { useVpUserGeneralSection } from './sections/useVpUserGeneralSection'
import { useVpUserProfilesSection } from './sections/useVpUserProfilesSection'
import { useFetchData } from '../../common/servercall/useFetchData'

export const VpUserDetail = () => {
    const { t } = useTranslation()
    const { id }: { id: string } = useParams()
    const customerOriginId = useSelector(customerOriginIdSelector)
    const { hasAccess: hasWriteAccess } = usePrivilege(FeaturePrivileges.enterpriseAdminMgmt.write)

    const fetchUser = useCallback(
        async (): Promise<VpUser | undefined> => {
            if (!customerOriginId) return
            const { data } = await api.get<VpUser>(`/api/enterprise/${customerOriginId}/vpUsers/${id}`)
            return data
        },
        [customerOriginId, id],
    )

    const { data, isFetching, error, refetch } = useFetchData<VpUser>(fetchUser)

    const pageInfo: Page | null = useMemo(
        () => data ? { label: `${data.lastName} ${data.firstName}`, key: 'vpuser-detail' } : null,
        [data],
    )

    const generalSection = useVpUserGeneralSection(id, isFetching, error, refetch, data)
    const profilesSection = useVpUserProfilesSection(id, data)

    const formSections = useMemo(() => [
        generalSection,
        profilesSection,
    ], [generalSection, profilesSection])

    return (
        <TitledBody title={t('admin-management.user-detail-title')} page={pageInfo}>
            <Grid container>
                <Grid item xs={12} lg={10}>
                    <SectionedForm formSections={formSections} readOnly={!hasWriteAccess} />
                </Grid>
            </Grid>
        </TitledBody>
    )
}
